import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    Alert,
    Button,
    Checkbox,
    Input,
    message,
    Modal,
    Select,
    Tag,
    Typography,
} from 'antd'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
    AgentContentJob,
    AgentContentJobMode,
    AgentContentJobStatus,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import {
    listAgentContentJobsForSection,
    startAgentContentJob,
} from '@/api/cb/agentContent'
import { AGENT_STATUS_TAG_COLOR } from '@/components/GlobalAgentActivity/agentActivityLabels'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { contentTreeStateAtom } from '@/atoms/contentTree'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import { syncErrorMessage } from '@/utils/syncMessages'
import styles from './CourseWideEditButton.module.less'

const MODELS: AnthropicModelName[] = [
    AnthropicModelName['claude-opus-5-5'],
    AnthropicModelName['claude-fable-5'],
    AnthropicModelName['claude-sonnet-5-5'],
]
const DEFAULT_MODEL = AnthropicModelName['claude-opus-5-5']

// Rough per-section wall-clock heuristic for the pre-run estimate shown in
// the confirm modal (AJ 2026-08-19: the scary confirm carries an estimate)
const EST_MINUTES_PER_SECTION_LOW = 2
const EST_MINUTES_PER_SECTION_HIGH = 4

const isActiveStatus = (status: AgentContentJobStatus): boolean =>
    status === AgentContentJobStatus.queued ||
    status === AgentContentJobStatus.waitingForLock ||
    status === AgentContentJobStatus.running

const jobStatusLabel = (status: AgentContentJobStatus): string =>
    status === AgentContentJobStatus.canceled
        ? 'Stopped'
        : status === AgentContentJobStatus.waitingForLock
          ? 'Waiting for lock'
          : status

const excerpt = (text: string | null): string =>
    text ? (text.length > 90 ? `${text.slice(0, 90)}…` : text) : ''

const HISTORY_LIMIT = 8

// The DANGEROUS course-wide edit entry: one instruction, the whole course.
// Deliberate friction by design — danger styling, explicit scope/cost copy,
// and an acknowledgement checkbox before the run button arms
export const CourseWideEditButton: React.FC<unknown> = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const activity = useRecoilValue(agentActivityAtom)
    const { flattenedContentTree } = useRecoilValue(contentTreeStateAtom)
    const setViewingState = useSetRecoilState(viewingStateAtom)

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [instruction, setInstruction] = useState<string>('')
    const [modelName, setModelName] =
        useState<AnthropicModelName>(DEFAULT_MODEL)
    const [hasAcknowledged, setHasAcknowledged] = useState<boolean>(false)
    const [isStarting, setIsStarting] = useState<boolean>(false)
    const [history, setHistory] = useState<AgentContentJob[]>([])

    // Recent course-wide runs, refreshed each time the modal opens (course
    // root = contentPath []; formula-sheet course passes share that hash, so
    // filter by mode)
    useEffect(() => {
        if (!isOpen) return
        let isMounted = true
        void (async (): Promise<void> => {
            const response = await listAgentContentJobsForSection({
                courseName,
                contentPath: [],
            })
            if (!isMounted || !response.data.success) return
            setHistory(
                response.data.payload.jobs
                    .filter(
                        (job) => job.mode === AgentContentJobMode.courseWideEdit
                    )
                    .sort(
                        (left, right) =>
                            new Date(right.createdAt).getTime() -
                            new Date(left.createdAt).getTime()
                    )
                    .slice(0, HISTORY_LIMIT)
            )
        })()
        return () => {
            isMounted = false
        }
    }, [courseName, isOpen])

    const sectionCount = flattenedContentTree.length
    const activeCourseJobs = useMemo(
        () =>
            activity?.activeJobs.filter(
                (job) =>
                    job.courseName === courseName && isActiveStatus(job.status)
            ) ?? [],
        [activity?.activeJobs, courseName]
    )
    // An in-flight course-wide edit gets its own callout: the server rejects
    // an identical second run, and "where is my run?" is the question the
    // modal gets reopened for
    const activeCourseWideRun = useMemo(
        () =>
            activeCourseJobs.find(
                (job) => job.mode === AgentContentJobMode.courseWideEdit
            ) ?? null,
        [activeCourseJobs]
    )
    const hasActiveCourseRun = activeCourseJobs.length > 0

    const openRunDetails = useCallback(
        (jobId: string): void => {
            // Close this modal first — the run drawer lives in the Agent
            // Activity modal, which the viewing state change opens
            setIsOpen(false)
            setViewingState((current) => ({
                ...current,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            }))
        },
        [setViewingState]
    )

    const startJob = useCallback(async (): Promise<void> => {
        setIsStarting(true)
        try {
            const response = await startAgentContentJob({
                courseName,
                contentPath: [],
                mode: AgentContentJobMode.courseWideEdit,
                modelName,
                instructorRequest: instruction.trim(),
            })
            if (!response.data.success) {
                syncErrorMessage(
                    `Failed to start course-wide edit: ${response.data.error}`
                )
                return
            }
            const jobId = response.data.payload.jobId
            setIsOpen(false)
            setHasAcknowledged(false)
            setInstruction('')
            message.success({
                content: (
                    <span>
                        {response.data.payload.waitingOnJobId
                            ? 'Course-wide edit queued behind another run — it starts automatically when that run finishes. '
                            : 'Course-wide edit started. '}
                        <Button
                            type={'link'}
                            size={'small'}
                            onClick={() => openRunDetails(jobId)}
                        >
                            View run
                        </Button>
                    </span>
                ),
                duration: 8,
            })
        } finally {
            setIsStarting(false)
        }
    }, [courseName, instruction, modelName, openRunDetails])

    return (
        <>
            <Button
                className={styles.dangerButton}
                danger
                onClick={() => setIsOpen(true)}
            >
                <AiSparkleIcon /> Course-Wide AI Edit
            </Button>
            <Modal
                destroyOnClose
                footer={null}
                onCancel={() => setIsOpen(false)}
                open={isOpen}
                title={
                    <span>
                        <AiSparkleIcon /> Course-wide AI edit — dangerous
                    </span>
                }
                width={'min(720px, 94vw)'}
            >
                <div className={styles.form}>
                    <Alert
                        description={
                            <ul>
                                <li>
                                    The agent checks out ALL{' '}
                                    <b>{sectionCount}</b> sections and applies
                                    your instruction across the entire course in
                                    one run.
                                </li>
                                <li>
                                    Estimated time if every section is touched:
                                    roughly{' '}
                                    {sectionCount * EST_MINUTES_PER_SECTION_LOW}
                                    –
                                    {sectionCount *
                                        EST_MINUTES_PER_SECTION_HIGH}{' '}
                                    minutes. Hard caps stop the run cleanly and
                                    apply the completed sections (marked
                                    partial).
                                </li>
                                <li>
                                    Every touched section gets before/after
                                    checkpoints, and the whole run can be undone
                                    with one click (Revert entire run, in the
                                    run details).
                                </li>
                                <li>
                                    The course is locked while it runs — other
                                    agent runs on this course wait for it.
                                </li>
                            </ul>
                        }
                        message={'This edits the ENTIRE course in one run'}
                        showIcon
                        type={'warning'}
                    />
                    {isReadOnly && (
                        <Alert
                            message={'This course is read-only for you.'}
                            showIcon
                            type={'error'}
                        />
                    )}
                    {activeCourseWideRun && (
                        <Alert
                            message={
                                <span>
                                    A course-wide edit is already{' '}
                                    {activeCourseWideRun.status ===
                                    AgentContentJobStatus.running
                                        ? 'running'
                                        : activeCourseWideRun.status ===
                                            AgentContentJobStatus.waitingForLock
                                          ? 'waiting to run'
                                          : 'queued'}{' '}
                                    on this course.{' '}
                                    <Button
                                        onClick={() =>
                                            openRunDetails(
                                                activeCourseWideRun.jobId
                                            )
                                        }
                                        size={'small'}
                                        type={'link'}
                                    >
                                        View run
                                    </Button>
                                </span>
                            }
                            showIcon
                            type={'warning'}
                        />
                    )}
                    {!activeCourseWideRun && hasActiveCourseRun && (
                        <Alert
                            message={
                                <span>
                                    Another agent run is active on this course —
                                    this run will queue behind it.{' '}
                                    <Button
                                        onClick={() =>
                                            openRunDetails(
                                                activeCourseJobs[0].jobId
                                            )
                                        }
                                        size={'small'}
                                        type={'link'}
                                    >
                                        View run
                                    </Button>
                                </span>
                            }
                            showIcon
                            type={'info'}
                        />
                    )}
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        disabled={isReadOnly}
                        maxLength={4000}
                        onChange={(event) => setInstruction(event.target.value)}
                        placeholder={
                            'The ONE instruction to apply across the whole course — e.g. "Standardize every notecard front to a question form", "Fix the units in all funding-methods formulas", "Add a worked example to every math problem missing one".'
                        }
                        value={instruction}
                    />
                    <div className={styles.controlsRow}>
                        <Select<AnthropicModelName>
                            disabled={isReadOnly}
                            onChange={setModelName}
                            options={MODELS.map((model) => ({
                                label: model,
                                value: model,
                            }))}
                            style={{ width: 220 }}
                            value={modelName}
                        />
                        <Typography.Text
                            className={styles.estimate}
                            type={'secondary'}
                        >
                            {sectionCount} sections in this course
                        </Typography.Text>
                    </div>
                    <Checkbox
                        checked={hasAcknowledged}
                        disabled={isReadOnly}
                        onChange={(event) =>
                            setHasAcknowledged(event.target.checked)
                        }
                    >
                        I understand this edits the whole course in one run and
                        may take hours.
                    </Checkbox>
                    <Button
                        danger
                        disabled={
                            isReadOnly ||
                            !hasAcknowledged ||
                            !instruction.trim() ||
                            !!activeCourseWideRun
                        }
                        loading={isStarting}
                        onClick={() => void startJob()}
                        type={'primary'}
                    >
                        Run course-wide edit
                    </Button>
                    {!!history.length && (
                        <div>
                            <Typography.Text strong>
                                Recent course-wide runs
                            </Typography.Text>
                            <div className={styles.historyList}>
                                {history.map((job) => (
                                    <div
                                        className={styles.historyRow}
                                        key={job.id}
                                    >
                                        <span>
                                            {new Date(
                                                job.createdAt
                                            ).toLocaleString()}
                                        </span>
                                        <Tag
                                            color={
                                                AGENT_STATUS_TAG_COLOR[
                                                    job.status
                                                ]
                                            }
                                        >
                                            {jobStatusLabel(job.status)}
                                        </Tag>
                                        {job.courseWideEdit && (
                                            <span>
                                                {
                                                    job.courseWideEdit
                                                        .sectionsApplied
                                                }
                                                /
                                                {
                                                    job.courseWideEdit
                                                        .sectionsTouched
                                                }{' '}
                                                sections
                                            </span>
                                        )}
                                        {job.courseWideEdit?.revertedAt && (
                                            <Tag color={'red'}>reverted</Tag>
                                        )}
                                        <span className={styles.historyExcerpt}>
                                            {excerpt(job.instructorRequest)}
                                        </span>
                                        <Button
                                            onClick={() =>
                                                openRunDetails(job.id)
                                            }
                                            size={'small'}
                                            type={'link'}
                                        >
                                            View run
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    )
}
